/**
 * Shared helpers for the event-ticketing surface (FRE-716).
 *
 * Keeps the affiliate-checkout URL and currency formatting logic in a single
 * place so `TicketCTA` and `TicketAsidePanel` can't drift apart.
 */

import { z } from 'zod';

/**
 * Affiliate-checkout service URL, validated with Zod. `NEXT_PUBLIC_*` env
 * vars are inlined by Next.js at build time so this module stays
 * browser-safe — the `@loci/config` package pulls in Node builtins
 * (`fs`, `path`, `dotenv`) via `loadEnv`, which breaks the Storybook Vite
 * bundle, so we validate here instead.
 */
const affiliateUrlSchema = z
  .string()
  .url({
    message:
      'NEXT_PUBLIC_AFFILIATE_CHECKOUT_URL must be a valid URL (e.g., http://localhost:4200 or https://checkout.freqseek.io)',
  })
  .default('http://localhost:4200');

function getAffiliateCheckoutUrl(): string {
  return affiliateUrlSchema.parse(process.env.NEXT_PUBLIC_AFFILIATE_CHECKOUT_URL || undefined);
}

export function buildAffiliateUrl(eventId: string): string {
  return `${getAffiliateCheckoutUrl()}/api/v1/checkout/${encodeURIComponent(eventId)}/redirect`;
}

export function formatTicketPrice(price: string | null, currency: string | null): string | null {
  if (!price) return null;
  const numeric = parseFloat(price.replace(/[^0-9.]/g, ''));
  if (Number.isNaN(numeric)) return price;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency ?? 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return price;
  }
}

export function isUsableHttpUrl(value: string | null | undefined): value is string {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
